import logger from './logger';
/**
 * エラーハンドリングユーティリティ
 * 非機能要件のセキュリティ要件とエラー処理要件を満たすエラー管理機能
 */

import { TimeoutError } from './apiTimeout';

// エラーレベル定義
export enum ErrorLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// エラーカテゴリ定義
export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NETWORK = 'network',
  TIMEOUT = 'timeout',
  SYSTEM = 'system',
  BUSINESS = 'business',
  EXTERNAL_API = 'external_api',
  DATABASE = 'database',
  UNKNOWN = 'unknown',
}

// セキュリティセンシティブなエラー
const SECURITY_SENSITIVE_ERRORS = [
  ErrorCategory.AUTHENTICATION,
  ErrorCategory.AUTHORIZATION,
  ErrorCategory.VALIDATION,
];

// 標準化されたエラー情報
export interface ErrorInfo {
  id: string;
  message: string;
  category: ErrorCategory;
  level: ErrorLevel;
  timestamp: string;
  metadata?: Record<string, any>;
  userMessage?: string;
  actionable?: boolean;
  retryable?: boolean;
  statusCode?: number;
  originalError?: Error;
}

// ユーザー向けエラーメッセージ
const USER_ERROR_MESSAGES = {
  [ErrorCategory.AUTHENTICATION]: '認証に失敗しました。ログイン情報を確認してください。',
  [ErrorCategory.AUTHORIZATION]: 'この操作を実行する権限がありません。',
  [ErrorCategory.VALIDATION]: '入力内容に問題があります。内容を確認してください。',
  [ErrorCategory.NETWORK]: 'ネットワーク接続に問題があります。しばらく待ってからお試しください。',
  [ErrorCategory.TIMEOUT]: '処理に時間がかかりすぎています。しばらく待ってからお試しください。',
  [ErrorCategory.SYSTEM]: 'システムエラーが発生しました。管理者にお問い合わせください。',
  [ErrorCategory.BUSINESS]: '業務ルールエラーが発生しました。',
  [ErrorCategory.EXTERNAL_API]: '外部サービスとの連携でエラーが発生しました。',
  [ErrorCategory.DATABASE]: 'データの処理でエラーが発生しました。',
  [ErrorCategory.UNKNOWN]: '予期しないエラーが発生しました。',
};

// リトライ可能なエラーカテゴリ
const RETRYABLE_CATEGORIES = [
  ErrorCategory.NETWORK,
  ErrorCategory.TIMEOUT,
  ErrorCategory.EXTERNAL_API,
];

/**
 * カスタムアプリケーションエラークラス
 */
export class AppError extends Error {
  public readonly id: string;
  public readonly category: ErrorCategory;
  public readonly level: ErrorLevel;
  public readonly timestamp: string;
  public readonly metadata: Record<string, any>;
  public readonly userMessage: string;
  public readonly actionable: boolean;
  public readonly retryable: boolean;
  public readonly statusCode?: number;
  public readonly originalError?: Error;

  constructor(
    message: string,
    category: ErrorCategory = ErrorCategory.UNKNOWN,
    level: ErrorLevel = ErrorLevel.MEDIUM,
    options: {
      metadata?: Record<string, any>;
      userMessage?: string;
      actionable?: boolean;
      retryable?: boolean;
      statusCode?: number;
      originalError?: Error;
    } = {}
  ) {
    super(message);

    this.name = 'AppError';
    this.id = this.generateErrorId();
    this.category = category;
    this.level = level;
    this.timestamp = new Date().toISOString();
    this.metadata = options.metadata || {};
    this.userMessage = options.userMessage || USER_ERROR_MESSAGES[category];
    this.actionable = options.actionable ?? this.isActionableByDefault();
    this.retryable = options.retryable ?? RETRYABLE_CATEGORIES.includes(category);
    this.statusCode = options.statusCode;
    this.originalError = options.originalError;

    // エラースタックの設定
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  private generateErrorId(): string {
    // RFC 4122 v4 UUID形式のエラーIDを生成
    const template = 'err_xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';

    return template.replace(/[xy]/g, char => {
      const randomValue = (Math.random() * 16) | 0;
      const hexValue = char === 'x' ? randomValue : (randomValue & 0x3) | 0x8;
      return hexValue.toString(16);
    });
  }

  private isActionableByDefault(): boolean {
    return [
      ErrorCategory.VALIDATION,
      ErrorCategory.BUSINESS,
      ErrorCategory.AUTHENTICATION,
    ].includes(this.category);
  }

  /**
   * エラー情報を構造化された形式で取得
   */
  toErrorInfo(): ErrorInfo {
    return {
      id: this.id,
      message: this.message,
      category: this.category,
      level: this.level,
      timestamp: this.timestamp,
      metadata: this.metadata,
      userMessage: this.userMessage,
      actionable: this.actionable,
      retryable: this.retryable,
      statusCode: this.statusCode,
      originalError: this.originalError,
    };
  }

  /**
   * ユーザー向けの安全なエラー情報を取得
   */
  toSafeUserInfo(): {
    id: string;
    message: string;
    actionable: boolean;
    retryable: boolean;
  } {
    return {
      id: this.id,
      message: this.userMessage,
      actionable: this.actionable,
      retryable: this.retryable,
    };
  }
}

/**
 * エラーハンドリングマネージャークラス
 */
class ErrorHandlerManager {
  private errorListeners: Array<(error: ErrorInfo) => void> = [];
  private logSensitiveInfo: boolean = false;

  constructor() {
    // 環境に応じてセンシティブ情報のログレベルを設定
    this.logSensitiveInfo = process.env.NODE_ENV === 'development';
  }

  /**
   * エラーリスナーを追加
   */
  addErrorListener(listener: (error: ErrorInfo) => void): void {
    this.errorListeners.push(listener);
  }

  /**
   * エラーリスナーを削除
   */
  removeErrorListener(listener: (error: ErrorInfo) => void): void {
    const index = this.errorListeners.indexOf(listener);
    if (index > -1) {
      this.errorListeners.splice(index, 1);
    }
  }

  /**
   * エラーを処理・通知
   */
  handleError(error: Error | AppError): ErrorInfo {
    const errorInfo = this.normalizeError(error);

    // ログ出力
    this.logError(errorInfo);

    // リスナーに通知
    this.notifyListeners(errorInfo);

    return errorInfo;
  }

  /**
   * 各種エラーを標準化されたAppErrorに変換
   */
  private normalizeError(error: Error | AppError): ErrorInfo {
    if (error instanceof AppError) {
      return error.toErrorInfo();
    }

    // タイムアウトエラーの処理
    if (error instanceof TimeoutError) {
      const appError = new AppError(error.message, ErrorCategory.TIMEOUT, ErrorLevel.MEDIUM, {
        metadata: {
          timeoutMs: error.timeoutMs,
          operationName: error.operationName,
        },
        originalError: error,
        retryable: true,
      });
      return appError.toErrorInfo();
    }

    // ネットワークエラーの処理
    if (error.name === 'NetworkError' || error.message.includes('fetch')) {
      const appError = new AppError(
        'ネットワーク接続エラー',
        ErrorCategory.NETWORK,
        ErrorLevel.MEDIUM,
        {
          originalError: error,
          retryable: true,
        }
      );
      return appError.toErrorInfo();
    }

    // 認証エラーの処理
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      const appError = new AppError('認証エラー', ErrorCategory.AUTHENTICATION, ErrorLevel.HIGH, {
        originalError: error,
        statusCode: 401,
      });
      return appError.toErrorInfo();
    }

    // 認可エラーの処理
    if (error.message.includes('403') || error.message.includes('Forbidden')) {
      const appError = new AppError('認可エラー', ErrorCategory.AUTHORIZATION, ErrorLevel.HIGH, {
        originalError: error,
        statusCode: 403,
      });
      return appError.toErrorInfo();
    }

    // その他のエラー
    const appError = new AppError(
      error.message || 'Unknown error',
      ErrorCategory.UNKNOWN,
      ErrorLevel.MEDIUM,
      {
        originalError: error,
      }
    );
    return appError.toErrorInfo();
  }

  /**
   * エラーログ出力
   */
  private logError(errorInfo: ErrorInfo): void {
    const logData = {
      timestamp: errorInfo.timestamp,
      id: errorInfo.id,
      category: errorInfo.category,
      level: errorInfo.level,
      message: errorInfo.message,
      metadata: errorInfo.metadata,
    };

    // セキュリティセンシティブなエラーの場合、本番環境では詳細を隠蔽
    if (SECURITY_SENSITIVE_ERRORS.includes(errorInfo.category) && !this.logSensitiveInfo) {
      logData.message = 'Security-sensitive error occurred';
      logData.metadata = { errorId: errorInfo.id };
    }

    switch (errorInfo.level) {
      case ErrorLevel.CRITICAL:
        logger.error('🚨 CRITICAL ERROR:', logData);
        break;
      case ErrorLevel.HIGH:
        logger.error('❌ HIGH ERROR:', logData);
        break;
      case ErrorLevel.MEDIUM:
        logger.warn('⚠️ MEDIUM ERROR:', logData);
        break;
      case ErrorLevel.LOW:
        console.info('ℹ️ LOW ERROR:', logData);
        break;
    }
  }

  /**
   * エラーリスナーに通知
   */
  private notifyListeners(errorInfo: ErrorInfo): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(errorInfo);
      } catch (err) {
        logger.error('Error listener failed:', err);
      }
    });
  }
}

// シングルトンインスタンス
const errorHandler = new ErrorHandlerManager();

/**
 * エラーハンドリング関数群
 */

/**
 * エラーを処理してErrorInfoを返す
 */
export function handleError(error: Error | AppError): ErrorInfo {
  return errorHandler.handleError(error);
}

/**
 * エラーを処理してユーザー向けの安全な情報を返す
 */
export function handleErrorSafely(error: Error | AppError): {
  id: string;
  message: string;
  actionable: boolean;
  retryable: boolean;
} {
  const errorInfo = errorHandler.handleError(error);
  return {
    id: errorInfo.id,
    message: errorInfo.userMessage || errorInfo.message,
    actionable: errorInfo.actionable || false,
    retryable: errorInfo.retryable || false,
  };
}

/**
 * Promise のエラーハンドリングラッパー
 */
export async function safeExecute<T>(
  operation: () => Promise<T>,
  errorContext?: string
): Promise<{ success: true; data: T } | { success: false; error: ErrorInfo }> {
  try {
    const data = await operation();
    return { success: true, data };
  } catch (error) {
    const errorInfo = handleError(error as Error);
    if (errorContext) {
      errorInfo.metadata = { ...errorInfo.metadata, context: errorContext };
    }
    return { success: false, error: errorInfo };
  }
}

/**
 * エラーリスナーの追加
 */
export function addErrorListener(listener: (error: ErrorInfo) => void): void {
  errorHandler.addErrorListener(listener);
}

/**
 * エラーリスナーの削除
 */
export function removeErrorListener(listener: (error: ErrorInfo) => void): void {
  errorHandler.removeErrorListener(listener);
}

/**
 * React Hook 用のエラーハンドリング
 */
export function useErrorHandler() {
  return {
    handleError,
    handleErrorSafely,
    safeExecute,
    AppError,
    ErrorLevel,
    ErrorCategory,
  };
}

/**
 * よく使用されるエラー作成ヘルパー
 */
export const ErrorHelpers = {
  validation: (message: string, field?: string) =>
    new AppError(message, ErrorCategory.VALIDATION, ErrorLevel.LOW, {
      metadata: { field },
      actionable: true,
    }),

  authentication: (message: string = '認証が必要です') =>
    new AppError(message, ErrorCategory.AUTHENTICATION, ErrorLevel.HIGH, {
      statusCode: 401,
      actionable: true,
    }),

  authorization: (message: string = 'アクセス権限がありません') =>
    new AppError(message, ErrorCategory.AUTHORIZATION, ErrorLevel.HIGH, {
      statusCode: 403,
      actionable: false,
    }),

  network: (message: string = 'ネットワークエラーが発生しました') =>
    new AppError(message, ErrorCategory.NETWORK, ErrorLevel.MEDIUM, { retryable: true }),

  timeout: (operationName: string, timeoutMs: number) =>
    new AppError(
      `${operationName} がタイムアウトしました (${timeoutMs}ms)`,
      ErrorCategory.TIMEOUT,
      ErrorLevel.MEDIUM,
      {
        metadata: { operationName, timeoutMs },
        retryable: true,
      }
    ),

  business: (message: string, metadata?: Record<string, any>) =>
    new AppError(message, ErrorCategory.BUSINESS, ErrorLevel.MEDIUM, {
      metadata,
      actionable: true,
    }),

  system: (message: string, originalError?: Error) =>
    new AppError(message, ErrorCategory.SYSTEM, ErrorLevel.CRITICAL, {
      originalError,
      actionable: false,
    }),
};
